import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { jwtDecode } from "jwt-decode";
import { message, Modal } from "antd";
import {
  fetchQuizByCourse,
  startQuizAttempt,
  fetchQuestionsForAttempt,
  submitQuizAttempt,
} from "../../../service/UserService/QuizAttemptService";
import useConfirmNavigation from "../../../components/user/myCourses/useConfirmNavigation";


export const formatTime = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

const buildUserResponses = (questions, userAnswers, userId, quizId, attempt) => {
  const now = new Date().toISOString().split(".")[0];
  return questions.map((question) => {
    let userAnswer = userAnswers[question.questionId] || [];
    if (
      question.questionType === "MCQ_SINGLE" ||
      question.questionType === "SHORT_ANSWER"
    ) {
      userAnswer = userAnswer ? [userAnswer] : [];
    }
    return {
      userId,
      quizId,
      questionId: question.questionId,
      attempt,
      userAnswer: JSON.stringify(userAnswer),
      answeredAt: now,
    };
  });
};

const parseScoreDetails = (result) => {
  try {
    return result?.scoreDetails
      ? JSON.parse(result.scoreDetails)
      : {
          correctAnswers: result?.correctAnswers,
          percentageScore: result?.percentageScore,
          totalScore: result?.totalScore,
        };
  } catch {
    return {
      correctAnswers: result?.correctAnswers,
      percentageScore: result?.percentageScore,
      totalScore: result?.totalScore,
    };
  }
};


export const useQuizAttempt = (courseId) => {
  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth);

  const [userId, setUserId] = useState(
    auth?.userId || localStorage.getItem("userId") || null
  );
  const [quiz, setQuiz] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [userAnswers, setUserAnswers] = useState({});
  const [markedForReview, setMarkedForReview] = useState({});
  const [visited, setVisited] = useState({});
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [start, setStart] = useState(false);
  const [isAcknowledged, setIsAcknowledged] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [error, setError] = useState("");
  const [quizAttemptId, setQuizAttemptId] = useState(null);
  const [currentAttemptNumber, setCurrentAttemptNumber] = useState(null);

  const currentQuestion = questions[currentIndex];

  useEffect(() => {
    if (!userId) {
      const token = localStorage.getItem("authtoken");
      if (token) {
        try {
          const decoded = jwtDecode(token);
          const id = decoded?.userId;
          if (id) {
            setUserId(id);
            localStorage.setItem("userId", id);
          }
        } catch (err) {
          console.error("Error decoding JWT:", err);
        }
      }
    }
  }, []);

  useEffect(() => {
    if (userId !== null && (!userId || userId <= 0)) {
      message.error("Invalid or missing user session. Please login again.");
      navigate("/login");
    }
  }, [userId]);

  useEffect(() => {
    if (start && currentQuestion) {
      setVisited((prev) => ({ ...prev, [currentQuestion.questionId]: true }));
    }
  }, [currentIndex, start]);

  useEffect(() => {
    const loadQuiz = async () => {
      try {
        const quizData = await fetchQuizByCourse(courseId);
        setQuiz(quizData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    loadQuiz();
  }, [courseId]);

  useEffect(() => {
    if (!start || timeLeft <= 0) return;
    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          message.warning("Time's up! Submitting quiz.");
          handleSubmit();
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [start, timeLeft]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (start) {
        e.preventDefault();
        e.returnValue = "Are you sure you want to leave? Your quiz will be auto-submitted.";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [start]);

  const handleStartQuiz = async () => {
    if (!quiz?.quizId) return;
    try {
      const attemptData = await startQuizAttempt(quiz.quizId, userId);
      const attemptId = attemptData?.quizAttemptId;
      const attemptNumber = attemptData?.attempt;
      const attemptsLeft = attemptData?.attemptsLeft ?? 0;

      if (attemptsLeft === 0) {
        Modal.warning({
          title: "No Attempts Left",
          content: "You have exhausted all your quiz attempts.",
        });
        return;
      }

      const qs = await fetchQuestionsForAttempt(quiz.quizId, attemptNumber, userId);
      setQuizAttemptId(attemptId);
      setCurrentAttemptNumber(attemptNumber);
      setQuestions(qs);
      setStart(true);
      setTimeLeft(quiz.timeLimit * 60);
    } catch (err) {
      message.error(err.message);
    }
  };

  const handleSubmit = useCallback(async () => {
    if (!quizAttemptId || !quiz?.quizId) {
      message.error("Missing quiz attempt information.");
      return;
    }
    setIsSubmitting(true);
    try {
      const userResponses = buildUserResponses(
        questions,
        userAnswers,
        userId,
        quiz.quizId,
        currentAttemptNumber
      );

      const result = await submitQuizAttempt(quizAttemptId, {
        userResponses,
        notes: "SUBMITTED",
        timeSpent: quiz.timeLimit * 60 - timeLeft,
      });

      message.success("Quiz submitted successfully!");

      const scoreDetails = parseScoreDetails(result);
      navigate(`/my-courses/course-content-user/${courseId}`, {
        state: {
          quizResult: {
            correctAnswers: scoreDetails.correctAnswers,
            percentageScore: scoreDetails.percentageScore,
            totalScore: scoreDetails.totalScore,
            passingScore: quiz.passingScore,
          },
        },
      });

      setStart(false);
      setUserAnswers({});
      setMarkedForReview({});
      setVisited({});
      setCurrentIndex(0);
      setQuizAttemptId(null);
      setCurrentAttemptNumber(null);
      setQuestions([]);
      setTimeLeft(0);
    } catch (err) {
      message.error(err.message);
    } finally {
      setIsSubmitting(false);
    }
  }, [quizAttemptId, quiz, questions, userAnswers, userId, currentAttemptNumber, timeLeft, courseId, navigate]);

  const confirmSubmit = useCallback(() => {
    Modal.confirm({
      title: "Submit Quiz?",
      content: "Are you sure you want to submit your quiz? You won't be able to change your answers after this.",
      okText: "Yes, Submit",
      cancelText: "No, Review Again",
      onOk: handleSubmit,
      centered: true,
      className: "quiz-confirm-modal",
      zIndex: 1000,
      okButtonProps: { style: { backgroundColor: "#910E11", borderColor: "#910E11" } },
      cancelButtonProps: { style: { borderColor: "#910E11", color: "#910E11" } },
    });
  }, [handleSubmit]);

  const handleAnswerChange = useCallback((questionId, value) => {
    setUserAnswers((prev) => ({ ...prev, [questionId]: value }));
  }, []);

  const toggleMarkForReview = useCallback((questionId) => {
    setMarkedForReview((prev) => ({ ...prev, [questionId]: !prev[questionId] }));
  }, []);

  const handleNext = useCallback(() => {
    setCurrentIndex((i) => Math.min(i + 1, questions.length - 1));
  }, [questions.length]);

  const handleBack = useCallback(() => {
    setCurrentIndex((i) => Math.max(i - 1, 0));
  }, []);

  const { ConfirmModal } = useConfirmNavigation(start, handleSubmit, isSubmitting);

  return {
    userId,
    quiz,
    questions,
    currentQuestion,
    currentIndex,
    setCurrentIndex,
    userAnswers,
    markedForReview,
    visited,
    loading,
    isSubmitting,
    start,
    isAcknowledged,
    setIsAcknowledged,
    timeLeft,
    error,
    handleStartQuiz,
    handleSubmit,
    confirmSubmit,
    handleAnswerChange,
    toggleMarkForReview,
    handleNext,
    handleBack,
    ConfirmModal,
  };
};